import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, LogOut, CreditCard, QrCode, Copy, Loader2, Clock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const fmt = (v) => `R$ ${(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export default function BlockedScreen() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pixDialog, setPixDialog] = useState(null);
  const [pixLoading, setPixLoading] = useState(false);

  useEffect(() => {
    api.get('/gestor/meu-financeiro')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const gerarPix = async (mensalidadeId) => {
    setPixLoading(true);
    try {
      const res = await api.post(`/gestor/meu-financeiro/${mensalidadeId}/gerar-pix`);
      setPixDialog(res.data);
      const r = await api.get('/gestor/meu-financeiro');
      setData(r.data);
    } catch (err) { toast.error('Erro ao gerar PIX'); }
    setPixLoading(false);
  };

  const copyPix = () => {
    if (pixDialog?.pix_code) {
      navigator.clipboard.writeText(pixDialog.pix_code);
      toast.success('Codigo PIX copiado!');
    }
  };

  const pendentes = data?.mensalidades?.filter(m => m.status === 'atrasado' || m.status === 'aguardando_pagamento') || [];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] p-6" data-testid="blocked-screen">
      <Card className="w-full max-w-lg bg-[#141414] border-[#27272A]">
        <CardHeader className="text-center pb-4">
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle className="w-8 h-8 text-red-400" />
          </div>
          <CardTitle className="text-2xl font-black font-['Barlow_Condensed'] tracking-tight uppercase text-white">
            ACESSO BLOQUEADO
          </CardTitle>
          <CardDescription className="text-zinc-400 mt-2">
            Sua conta esta bloqueada por inadimplencia. Regularize para reativar o acesso.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="w-4 h-4 text-red-400" />
              <p className="text-sm font-bold text-red-400 uppercase tracking-wider">Pagamento Pendente</p>
            </div>
            <p className="text-sm text-zinc-400">
              Voce possui mensalidades em atraso ha mais de 5 dias. Gere o PIX abaixo e pague para reativar.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
            </div>
          ) : pendentes.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-zinc-500">Mensalidades pendentes</p>
              {pendentes.map(m => (
                <div key={m.id} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A] border border-[#27272A]" data-testid={`blocked-mens-${m.id}`}>
                  <div>
                    <p className="text-sm text-white font-medium">Comp. {m.competencia}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <p className="text-xs text-zinc-500">Venc: {m.data_vencimento}</p>
                      <Badge className="bg-red-400/10 text-red-400 text-[10px]">{m.status === 'atrasado' ? 'Atrasado' : 'Pendente'}</Badge>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold font-['Barlow_Condensed'] text-white">{fmt(m.valor)}</p>
                    <Button
                      size="sm"
                      onClick={() => m.pix_code ? setPixDialog({ pix_code: m.pix_code, valor: m.valor }) : gerarPix(m.id)}
                      disabled={pixLoading}
                      className="bg-[#007AFF] hover:bg-[#0066DD]"
                      data-testid={`blocked-pix-${m.id}`}
                    >
                      {pixLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#27272A]">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                <p className="text-sm text-zinc-300">Aguardando confirmacao do administrador.</p>
              </div>
            </div>
          )}

          <Button
            onClick={logout}
            variant="outline"
            className="w-full border-[#27272A] text-zinc-300 hover:bg-[#1C1C1E]"
            data-testid="blocked-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sair
          </Button>
        </CardContent>
      </Card>

      <Dialog open={!!pixDialog} onOpenChange={() => setPixDialog(null)}>
        <DialogContent className="bg-[#141414] border-[#27272A] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-xl tracking-tight uppercase flex items-center gap-2">
              <QrCode className="w-5 h-5 text-[#007AFF]" /> Pagar PIX
            </DialogTitle>
          </DialogHeader>
          {pixDialog && (
            <div className="space-y-4 text-center" data-testid="blocked-pix-dialog">
              <p className="text-2xl font-black font-['Barlow_Condensed'] text-white">{fmt(pixDialog.valor)}</p>
              <div className="bg-white p-4 rounded-lg inline-block mx-auto"><QRCodeSVG value={pixDialog.pix_code} size={200} /></div>
              <div>
                <p className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Copia e Cola</p>
                <div className="flex gap-2">
                  <Input value={pixDialog.pix_code} readOnly className="bg-[#0A0A0A] border-[#27272A] text-xs" data-testid="blocked-pix-code-input" />
                  <Button size="icon" variant="outline" onClick={copyPix} className="border-[#27272A] shrink-0" data-testid="blocked-copy-pix"><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <p className="text-xs text-zinc-500">Apos pagar, aguarde a confirmacao do administrador para liberar seu acesso.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
